'use client';

import React, { useState, useEffect } from 'react';
import { QuickCalculatorForm } from '@/components/quick-calculator/QuickCalculatorForm';
import { getYantraLibrary, recommendYantra, YantraItem, YantraRecommendationResult, CreateBirthProfileInput } from '@/lib/api/client';
import { CalculatorPageContent } from '@/components/calculators/CalculatorPageContent';
import { Sparkles, Grid } from 'lucide-react';

export default function YantraCalculatorPage() {
  const [library, setLibrary] = useState<YantraItem[]>([]);
  const [recommendation, setRecommendation] = useState<YantraRecommendationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getYantraLibrary()
      .then(data => setLibrary(data))
      .catch(err => console.error('Failed to load Yantra library:', err));
  }, []);

  const handleRecommend = async (input: CreateBirthProfileInput) => {
    setLoading(true);
    setError(null);
    try {
      const data = await recommendYantra(input);
      setRecommendation(data);
    } catch (err: any) {
      setError(err.message || 'Failed to generate chart-driven Yantra recommendation');
      setRecommendation(null);
    } finally {
      setLoading(false);
    }
  };

  const introParagraph = "Yantras are ancient sacred geometric diagrams and 3x3 numeral magic squares used in Vedic tradition as contemplative tools to focus specific planetary energies. Our calculator analyzes your natal Shadbala strengths to transparently recommend which planetary energy needs support.";

  const methodologyContent = (
    <div className="space-y-3">
      <p>
        Our recommendation engine evaluates all 7 Grahas using Swiss Ephemeris (pyswisseph) Shadbala computations.
      </p>
      <p>
        It identifies the planet with the lowest strength ratio relative to its classical Parashari Rupa threshold, suggesting the corresponding Navagraha numeral Yantra square for focused meditation.
      </p>
    </div>
  );

  const subsections = [
    {
      title: "3x3 Numeral Magic Squares",
      content: "Each of the 9 Navagraha Yantras is defined by a unique 3x3 matrix of numbers whose rows, columns, and diagonals sum to a constant sacred number (e.g. Sun Yantra magic constant = 15)."
    },
    {
      title: "Transparent Recommendation Logic",
      content: "Unlike black-box tools, our engine shows the exact Shadbala Rupa score and minimum requirement for every planet so you can inspect why a specific Yantra is suggested."
    },
    {
      title: "Focus & Meditation Tool",
      content: "In classical philosophy, Yantras serve as visual anchor points for meditation and mental alignment—helping cultivate discipline and planetary awareness."
    }
  ];

  const faqs = [
    {
      q: "What is a Yantra?",
      a: "A Yantra is a geometric or numerical sacred diagram used in Vedic traditions for meditation, focus, and planetary energy alignment."
    },
    {
      q: "How does AstroPredict recommend a Yantra?",
      a: "AstroPredict evaluates planetary dignities and weak functional benefic planets to identify supportive Yantras."
    },
    {
      q: "What is a 3x3 Numeral Yantra Magic Square?",
      a: "It is a 3x3 grid of numbers where all rows, columns, and diagonals add up to the same constant sum dedicated to a specific planet."
    },
    {
      q: "Is this Yantra Calculator free?",
      a: "Yes, 100%! All calculations on AstroPredict are completely free with zero product sales or paywalls."
    }
  ];

  const relatedCalculators = [
    {
      title: "Shadbala Calculator",
      href: "/shadbala-calculator",
      description: "Inspect full 6-fold planetary strength scores behind the Yantra recommendation.",
      categoryBadge: "Strength"
    },
    {
      title: "Birth Chart Calculator",
      href: "/birth-chart-calculator",
      description: "Generate your complete Kundli to view planetary house positions.",
      categoryBadge: "Core"
    },
    {
      title: "Moon Sign Calculator",
      href: "/moon-sign-calculator",
      description: "Discover your true Chandra Rashi for mind alignment.",
      categoryBadge: "Lunar"
    }
  ];

  return (
    <div className="space-y-12 max-w-5xl mx-auto py-4">
      {/* Page Header */}
      <div className="bg-[var(--card)] border border-[var(--line)] rounded-2xl p-6 sm:p-8 shadow-xs space-y-3">
        <span className="text-xs font-semibold uppercase tracking-wider text-[var(--gold)] block">
          Sacred Geometric &amp; Numeral Rectifiers
        </span>
        <h1 className="text-2xl sm:text-3xl font-serif font-medium text-[var(--foreground)] flex items-center gap-2">
          <Grid className="w-8 h-8 text-[var(--gold)]" />
          <span>Navagraha Yantra Calculator</span>
        </h1>
        <p className="text-xs sm:text-sm text-[var(--muted)] max-w-3xl leading-relaxed">
          Explore the complete static library of all 9 Navagraha Yantras with verified 3x3 numeral magic squares, or generate a chart-driven recommendation based on your birth chart&apos;s lowest Shadbala planetary strength ratio.
        </p>
      </div>

      {/* SECTION 1: CHART-DRIVEN RECOMMENDATION */}
      <div className="space-y-6">
        <QuickCalculatorForm
          title="1. Chart-Driven Recommendation (Shadbala Engine)"
          subtitle="Enter birth details to analyze your 7 classical grahas and discover which planet requires energetic strengthening."
          onCalculate={handleRecommend}
          loading={loading}
        />

        {error && (
          <div className="bg-rose-500/10 border border-rose-500/30 rounded-xl p-4 text-rose-500 text-sm">
            {error}
          </div>
        )}

        {recommendation && (
          <div className="bg-[var(--card)] border border-[var(--gold)]/30 rounded-2xl p-6 shadow-xs space-y-4">
            <h2 className="text-xl font-serif font-medium text-[var(--gold)]">
              Recommended Yantra: {recommendation.recommendedYantra?.sanskritName}
            </h2>
            <p className="text-xs text-[var(--muted)]">
              Target Planet: <span className="text-[var(--foreground)] font-semibold">{recommendation.recommendedPlanet}</span> | Reasoning: {recommendation.reason}
            </p>
          </div>
        )}
      </div>

      <CalculatorPageContent
        calculatorName="Navagraha Yantra Calculator"
        introParagraph={introParagraph}
        methodologyTitle="How I Recommend Planetary Yantras"
        methodologyContent={methodologyContent}
        subsections={subsections}
        faqs={faqs}
        relatedCalculators={relatedCalculators}
        showSignPeriodGrid={false}
      />
    </div>
  );
}
